function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useContext, useEffect } from 'react';
import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import SelectMenuComponent from './SelectMenuComponent';
import { Tabs } from '../../../Tabs';
import { Text } from '../../../Text';
import { ARROW_DOWN_KEY, ARROW_UP_KEY, PLACEMENT_TOP_LEFT, PLACEMENT_TOP_RIGHT, PLACEMENT_BOTTOM_RIGHT } from '../../../Utils/constant';
import { SelectMenuContext } from '../../context';
import SearchBar from '../SelectMenuBody/SearchBar';
import styles from '@dtsl/css-select-menu/dist/index.module.css';
function SelectMenuBody(props) {
  const {
    isLoading = false,
    onTabSelect = () => {},
    tabMetaData = [],
    menuClassName = '',
    activeTabIndex = 0,
    isControlButton = false,
    searchInputProps = {},
    searchPlaceholder = 'Search',
    isSearchClearable = true,
    handleResetSearch = () => {},
    inlineSelectMenuPanelStyles = {},
    stickyComponent = null,
    isExactMatch = true,
    isCreatable = false,
    creatableButtonMetaData = {
      disabled: false,
      createLabelText: 'Create',
      handleCreateOption: () => {}
    },
    inputStyles = {},
    panelDataTestId = ''
  } = props;
  const {
    isOpen,
    focused,
    isMobile,
    setIsOpen,
    setFocused,
    searchValue,
    handleSearch,
    isOverlayOpen,
    setIsOverlayOpen,
    isPanelSearchable,
    selectMenuControlRef,
    selectMenuGroupListRef,
    selectMenuSelectAllRef,
    selectMenuChildFolderRef,
    selectMenuBodyRef,
    bodyPosition,
    floatingUiProps,
    selectMenuWidth
  } = useContext(SelectMenuContext);
  const menuClass = classNames({
    [styles['sib-selectmenu--panel']]: true,
    [PLACEMENT_TOP_LEFT]: bodyPosition === 'topLeft',
    [PLACEMENT_TOP_RIGHT]: bodyPosition === 'topRight',
    [PLACEMENT_BOTTOM_RIGHT]: bodyPosition === 'bottomRight',
    [menuClassName]: !!menuClassName
  });
  const handleResize = () => {
    if (selectMenuBodyRef.current) {
      if (isMobile && isOverlayOpen) {
        setIsOverlayOpen(false);
        searchValue && handleSearch('');
      } else {
        setIsOpen(false);
        searchValue && handleSearch('');
      }
      selectMenuControlRef.current?.classList.remove(styles['sib-selectmenu-control-focused']);
    }
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  //Set focused value as null on each tab change
  const handleTabSelect = (tab, index, event) => {
    event.preventDefault();
    setFocused(null);
    onTabSelect(tab, index, event);
  };
  const handleNextOption = (e, firstOption, nextOption, lastOption, optionList) => {
    const selectedOptIndex = focused === lastOption ? firstOption : nextOption;
    Array.from(optionList).forEach(optionList => {
      if (optionList.classList.contains(styles['focused'])) {
        optionList.classList.remove(styles['focused']);
        optionList.setAttribute('tabindex', '');
      }
    });
    setFocused(selectedOptIndex);
    optionList[selectedOptIndex]?.setAttribute('tabindex', '0');
    optionList[selectedOptIndex]?.classList.add(styles['focused']);
    optionList[selectedOptIndex]?.focus();
  };
  const handleKeyPress = event => {
    if (selectMenuBodyRef && selectMenuBodyRef.current) {
      let navigate = false;
      const _panelList = selectMenuBodyRef.current.children[1]?.children[0]?.children;
      let optionList = [];
      const _childFolder = selectMenuChildFolderRef?.current;
      const _childFolderList = _childFolder?.children[1]?.children[0]?.children;
      const _parentFolder = _childFolder?.previousSibling;
      if (!selectMenuSelectAllRef.current) {
        navigate = true;
        //Check for Child folder list items
        if (_childFolder && _childFolder?.style.display == 'block') {
          optionList.push(_childFolder.children[0]); //folder header added as 1st item in list
          if (selectMenuGroupListRef && selectMenuGroupListRef.current) {
            Array.from(_childFolderList).forEach(group => {
              Array.from(group.children[1]?.children).forEach(groupChild => {
                if (!groupChild.classList.contains(styles['locked'])) {
                  optionList.push(groupChild); //Group Options
                }
              });
            });
          } else {
            Array.from(_childFolderList).forEach(folderChildItem => {
              if (!folderChildItem.classList.contains(styles['locked'])) {
                optionList.push(folderChildItem); //Single Options
              }
            });
          }
        } else {
          if (selectMenuGroupListRef && selectMenuGroupListRef.current) {
            Array.from(_panelList).forEach(group => {
              if (group.classList.contains(styles['select-all'])) {
                if (!group.classList.contains(styles['locked'])) {
                  optionList.push(group);
                }
              } else {
                Array.from(group.children[1]?.children).forEach(groupChild => {
                  if (!groupChild.classList.contains(styles['locked'])) {
                    optionList.push(groupChild); //Group Options
                  }
                });
              }
            });
          } else {
            Array.from(_panelList).forEach(panelListItem => {
              if (!panelListItem.classList.contains(styles['locked'])) {
                optionList.push(panelListItem); //Single Options
              }
            });
          }
        }
      } else {
        Array.from(_panelList).forEach(panelListItem => {
          if (!panelListItem.classList.contains(styles['locked'])) {
            optionList.push(panelListItem); //Single Options
          }
        });
        if (_parentFolder?.style.display == 'block') {
          navigate = true;
        }
      }
      if (navigate) {
        if (event.key === ARROW_UP_KEY && !event.target.classList.contains(styles['sib-selectmenu-options'])) {
          const last = optionList.length - 1;
          const next = parseInt(focused) - 1;
          handleNextOption(event, last, next, 0, optionList);
        }
        if (event.key === ARROW_DOWN_KEY) {
          const first = 0;
          const next = focused === null ? 0 : parseInt(focused) + 1;
          handleNextOption(event, first, next, optionList.length - 1, optionList);
        }
      }
    }
  };
  const {
    handleCreateOption,
    disabled: isCreateButtonDisabled,
    createLabelText,
    ...restCreatableProps
  } = creatableButtonMetaData;
  const handleCreateOptionClick = (label, event) => {
    event.preventDefault();
    event.nativeEvent.stopImmediatePropagation();
    handleCreateOption(label, event);
    handleSearch('');
  };
  const {
    refs,
    floatingStyles,
    context
  } = floatingUiProps;
  const selectMenuWidthStyle = selectMenuWidth ? {
    width: selectMenuWidth
  } : {};
  const selectMenuPanel = () => {
    return /*#__PURE__*/React.createElement(FloatingPortal, null, /*#__PURE__*/React.createElement(FloatingFocusManager, {
      context: context,
      initialFocus: -1,
      visuallyHiddenDismiss: true,
      returnFocus: false
    }, /*#__PURE__*/React.createElement("div", {
      ref: refs.setFloating,
      style: {
        ...floatingStyles
      }
    }, /*#__PURE__*/React.createElement("div", {
      ref: selectMenuBodyRef,
      className: menuClass,
      "aria-expanded": "true",
      role: "listbox",
      "data-testid": panelDataTestId ? panelDataTestId : null,
      style: isControlButton ? {
        minWidth: 'max-content',
        ...selectMenuWidthStyle,
        ...inlineSelectMenuPanelStyles
      } : {
        ...selectMenuWidthStyle,
        ...inputStyles,
        ...inlineSelectMenuPanelStyles
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['sib-selectmenu--toolbar']
    }, isPanelSearchable && /*#__PURE__*/React.createElement("div", {
      className: styles['sib-selectmenu--searchbar']
    }, /*#__PURE__*/React.createElement(SearchBar, {
      searchValue: searchValue,
      handleSearch: handleSearch,
      handleResetSearch: handleResetSearch,
      searchInputProps: searchInputProps,
      searchPlaceholder: searchPlaceholder,
      isSearchClearable: isSearchClearable
    })), tabMetaData.length > 0 && !isLoading && /*#__PURE__*/React.createElement(Tabs, {
      tabs: tabMetaData,
      activeIndex: activeTabIndex,
      onSelect: handleTabSelect,
      tabStyles: {
        paddingTop: '8px'
      },
      tabListStyles: {
        padding: '0 16px'
      }
    }, /*#__PURE__*/React.createElement(React.Fragment, null))), /*#__PURE__*/React.createElement("div", {
      className: `${styles['sib-selectmenu-options-panel']} ${tabMetaData.length == 0 && !isPanelSearchable ? styles['sib-selectmenu-empty-toolbar'] : ''}`,
      role: tabMetaData.length > 0 ? 'tabpanel' : 'presentation',
      id: tabMetaData.length > 0 ? `tabpanel-tab-${activeTabIndex}` : null,
      onKeyDown: handleKeyPress
    }, /*#__PURE__*/React.createElement(SelectMenuComponent, _extends({}, props, {
      simpleSelectMenuTabIndex: 0,
      isCreateBtnPresent: isCreatable && !isExactMatch
    }))), isCreatable && !isExactMatch ? /*#__PURE__*/React.createElement("div", {
      className: styles['sib-selectmenu__creatableselect-wrapper']
    }, /*#__PURE__*/React.createElement("button", _extends({}, restCreatableProps, {
      className: styles['sib-selectmenu__creatableselect-btn'],
      onClick: event => handleCreateOptionClick(searchValue, event),
      disabled: isCreateButtonDisabled ? true : false,
      "aria-disabled": isCreateButtonDisabled ? true : false,
      type: "button"
    }), /*#__PURE__*/React.createElement(Text, {
      size: "lg",
      fontFamily: "interactive"
    }, createLabelText.trim()), /*#__PURE__*/React.createElement(Text, null, "\xA0\"", searchValue, "\""))) : null, stickyComponent))));
  };
  if (isMobile) {
    return isOverlayOpen ? selectMenuPanel() : null;
  }
  return isOpen ? selectMenuPanel() : null;
}
SelectMenuBody.propTypes = {
  menuClassName: PropTypes.string,
  activeTabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tabMetaData: PropTypes.array,
  inlineSelectMenuPanelStyles: PropTypes.object,
  isLoading: PropTypes.bool,
  placement: PropTypes.string,
  isControlButton: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  handleSearch: PropTypes.func,
  handleResetSearch: PropTypes.func,
  isSearchClearable: PropTypes.bool,
  searchInputProps: PropTypes.object,
  onTabSelect: PropTypes.func,
  stickyComponent: PropTypes.node,
  creatableButtonMetaData: PropTypes.object,
  isExactMatch: PropTypes.bool,
  isCreatable: PropTypes.bool,
  inputStyles: PropTypes.object,
  panelDataTestId: PropTypes.string
};
export default SelectMenuBody;